import { CONTACT_TYPE } from '../../common/constants'
import { ContactAsyncSelectOption, Owner, PageListParams, SelectOption } from '../globalTypes'
import { getQueryData } from '../helpers'
import { Contact, ContactData } from './types'

// Narrow structural shape both Contact and Owner satisfy - only the fields the option formatters below actually read
interface ContactFormOption {
    id: number
    internalAppId?: number | null
    contactType: string
    contactPerson?: { firstName?: string | null; lastName?: string | null } | null
    contactCompany?: { name?: string | null } | null
}

export interface CascadeContactOption {
    value: number
    id: number
    label: string
    name: string
    email: string | null | undefined
    contactPersonCompany: Contact['contactPersonCompany']
}

export interface CascadeCompanyOption {
    value: number
    id: number
    label: string
    name: string
}

/**
 * Retrieves contact field options from React Query cache
 * @param contactId - Contact ID to fetch from cache
 * @param field - Which field to extract options for
 * @returns Select options array for the requested field
 */
export const formatContactFieldOptions = (
    contactId: string,
    field: 'contactPerson' | 'contactPersonCompany' | 'contactCompany',
    params?: PageListParams
): SelectOption[] => {
    const queryKey = field === 'contactCompany' ? ['companies', contactId] : ['contacts', contactId]
    const data = getQueryData<ContactData>(queryKey, params)

    if (!data?.item) return []

    const { item } = data

    switch (field) {
        case 'contactPerson':
            return item.contactPerson ? [{ value: item.id.toString(), label: `${item.contactPerson.firstName} ${item.contactPerson.lastName}` }] : []

        case 'contactPersonCompany':
            return item.contactPersonCompany?.length
                ? item.contactPersonCompany.map((cpc) => ({
                      value: cpc.company?.id?.toString(),
                      label: cpc.company?.contactCompany?.name ?? '',
                  }))
                : []

        case 'contactCompany':
            return item.contactCompany ? [{ value: item.id.toString(), label: item.contactCompany.name }] : []

        default:
            return []
    }
}

export const formatContactAsyncOption = (item: Contact): ContactAsyncSelectOption => {
    const isPersonEntity = item.contactType === 'person'
    const baseName = isPersonEntity
        ? `${item.contactPerson?.firstName ?? ''} ${item.contactPerson?.lastName ?? ''}`.trim()
        : (item.contactCompany?.name ?? '')

    return {
        value: item.id,
        label: `ID: ${item.internalAppId} - ${baseName}`,
        data: {
            internalAppId: item.internalAppId,
            contactEmail: item.contactEmail,
            ...(isPersonEntity ? { contactPersonCompany: item.contactPersonCompany ?? [] } : {}),
        },
    }
}

export const formatCascadeContactOptions = (items: Contact[]): CascadeContactOption[] => {
    const options: CascadeContactOption[] = []

    items.forEach((item) => {
        const personName = `${item.contactPerson?.firstName ?? ''} ${item.contactPerson?.lastName ?? ''}`.trim()
        const baseLabel = `ID: ${item.internalAppId} - ${personName}`
        const baseOption = {
            value: item.id as number,
            id: item.id as number,
            name: baseLabel,
            contactPersonCompany: item.contactPersonCompany,
        }

        if (item.contactEmail && item.contactEmail.length > 1) {
            item.contactEmail.forEach((emailItem) => {
                options.push({ ...baseOption, label: `${baseLabel}, ${emailItem.email}`, email: emailItem.email })
            })
        } else {
            const email = item.contactEmail?.[0]?.email
            options.push({ ...baseOption, label: email ? `${baseLabel}, ${email}` : baseLabel, email })
        }
    })
    return options
}

export const formatCascadeCompanyOptions = (items: Contact[]): CascadeCompanyOption[] =>
    items.map((item) => ({
        value: item.id as number,
        id: item.id as number,
        label: item.contactCompany?.name ?? '',
        name: item.contactCompany?.name ?? '',
    }))

/**
 * Formats a single Contact/Owner entity as a single-item select option, used to show the initial selected value
 */
const formatContactOption = (entity?: ContactFormOption | Owner | null): SelectOption[] => {
    if (!entity?.id) return []

    if (entity.contactType === CONTACT_TYPE.PERSON) {
        const optionLabel = `ID: ${entity.internalAppId} - ${entity.contactPerson?.firstName} ${entity.contactPerson?.lastName}`
        return [{ value: entity.id.toString(), label: optionLabel }]
    }

    return [{ value: entity.id.toString(), label: `ID: ${entity.internalAppId} - ${entity.contactCompany?.name}` }]
}

/**
 * Formats a Contact/Owner entity or list of entities as select options, used to show initial selected value(s)
 */
export const formatContactOptions = (data?: ContactFormOption | Owner | (ContactFormOption | Owner)[] | null): SelectOption[] => {
    if (Array.isArray(data)) {
        return data.flatMap(formatContactOption)
    }

    return formatContactOption(data)
}
